/**
 * AtlasNightSkyBodiesModule — REAL MESHES in the night sky (2026-08-27 pt2):
 *
 *   STATION   the actual `prop:space_station` GLB crossing the sky every six
 *             minutes (night_sky_schedule.stationAt drives it) — "very tiny,
 *             not as tiny as a dot": ~1.2° of sky, slowly tumbling, its own
 *             albedo unlit-warm (the station IS in sunlight up there even
 *             when the ground is dark).
 *   CRUISER   the `prop:ship_sovereign` capital hull during the shared war
 *             window (battleSkyAt — the same clock the space sector fights
 *             on), drifting along its heading; the shader battle layer keeps
 *             the weapon flashes + escort train around it.
 *   PLANETS   the home system's ACTUAL worlds — Cindral, Vorune, Palewater,
 *             Ashfall (space_sector PLANETS, same textures) — as small
 *             textured discs riding the ecliptic, none wider than the Moon
 *             (0.5°; the 2026-09-08 "floating structure over the town" was
 *             Vorune at 2.4° + a 1.5× halo) (planetsAt: heliocentric
 *             orbits over real days, so every night they've crept along).
 *             Planets hold STEADY (no twinkle) against the shimmering stars —
 *             exactly how you tell a planet from a star with the naked eye.
 *
 * Rendering doctrine (the sky-traffic/volcanic-planet recon, all four traps):
 *   · Anchored to `atlas.localX/Y/Z` + dir·R each frame (origin-rebase-safe;
 *     `camera.getWorldPosition` is render-space and drifts past 10 km).
 *   · R = 3600 m — inside the 5200 m far plane, far above the 900–1500 m
 *     cloud band; meshes are OPAQUE with depthWrite ON so the mesh-cloud
 *     field (transparent, depthTest on) correctly composites OVER them.
 *   · `material.fog = false` on EVERY material — night FogExp2 at 3.6 km is
 *     e^-(6.5)² ≈ total; this is the first fog opt-out in the client.
 *   · `frustumCulled = false`; never registered with RenderCullingModule.
 *   · Unlit (MeshBasicMaterial keeping the GLB albedo maps): scene lights
 *     are ground-local; sky bodies carry their own sunlight.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { makeSystem, Phase, Sub } from '../engine-compat.js';
import { prefabAssetId } from '../lib/player_models.js';
import { markShared } from '../lib/defer_dispose.js';
import { currentTod } from './SkyModule.js';
import { nightFactor, sunDirection } from '../lib/lighting.js';
import {
  stationAt, battleSkyAt, planetsAt, PLANET_DEFS, type Vec3,
} from '../lib/night_sky_schedule.js';
import type { AssetModule } from './AssetModule.js';

const R = 3600;                    // sky anchor distance (angular size is all that matters)
const STATION_DEG = 1.2;           // station angular diameter
const CRUISER_DEG = 1.6;           // capital hull angular length
const deg2size = (deg: number): number => 2 * R * Math.tan((deg * Math.PI / 180) / 2);

/** Box-fit an object so its LONGEST axis spans `size` meters. */
function fitLongest(obj: THREE.Object3D, size: number): void {
  const box = new THREE.Box3().setFromObject(obj);
  const s = new THREE.Vector3(); box.getSize(s);
  const longest = Math.max(s.x, s.y, s.z, 0.001);
  obj.scale.setScalar(size / longest);
}

/** Replace PBR materials with unlit fog-free ones keeping the albedo maps. */
function unlitify(obj: THREE.Object3D, tint: THREE.Color): void {
  const conv = (old: THREE.Material): THREE.Material => {
    const std = old as THREE.MeshStandardMaterial;
    const mat = new THREE.MeshBasicMaterial({ map: std.map ?? null, color: tint.clone() });
    mat.fog = false;
    return mat;
  };
  obj.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    m.material = Array.isArray(m.material) ? (m.material.map(conv) as unknown as THREE.Material) : conv(m.material as THREE.Material);
    m.frustumCulled = false;
  });
}

interface PlanetMesh { root: THREE.Group; disc: THREE.Mesh; mat: THREE.MeshBasicMaterial; rim: THREE.MeshBasicMaterial }

export class AtlasNightSkyBodiesModule implements IModule {
  readonly name = 'AtlasNightSkyBodies';
  private app: App | null = null;
  private assets: AssetModule | null = null;
  private station: THREE.Object3D | null = null;
  private cruiser: THREE.Object3D | null = null;
  private planets: PlanetMesh[] = [];
  private tried = false;
  private readonly _v = new THREE.Vector3();

  init(app: App): void {
    this.app = app;
    this.assets = app.getModule('AssetModule') as AssetModule | null;
    // Planet discs need no GLBs — build them now; textures stream in after.
    this.buildPlanets(app.world.scene);
    app.world.addSystem(makeSystem('NightSkyBodies', Phase.RenderSub, 6, () => this.tick(), Sub.Update));
    (globalThis as Record<string, unknown>).__nightSkyBodies = () => {
      // Probe: where a body is and how big it LOOKS (its longest world axis
      // over its distance from the player, in degrees) — the "floating
      // structure over the town" report (2026-09-08) was a sky body.
      const body = (o: THREE.Object3D | null): { visible: boolean; at: number[]; distM: number; longestM: number; apparentDeg: number; yaw: number; pitch: number } | null => {
        if (!o) return null;
        const b = new THREE.Box3().setFromObject(o); const sz = new THREE.Vector3(); b.getSize(sz);
        const dx = o.position.x - atlas.localX, dy = o.position.y - (atlas.localY + 1.6), dz = o.position.z - atlas.localZ;
        const d = Math.hypot(dx, dy, dz);
        const longest = Math.max(sz.x, sz.y, sz.z);
        return {
          visible: o.visible, at: [Math.round(o.position.x), Math.round(o.position.y), Math.round(o.position.z)], distM: Math.round(d), longestM: +longest.toFixed(1),
          apparentDeg: +((2 * Math.atan(longest / 2 / Math.max(1, d))) * 180 / Math.PI).toFixed(2),
          yaw: +Math.atan2(-dx, -dz).toFixed(3), pitch: +Math.atan2(dy, Math.hypot(dx, dz)).toFixed(3), // forward = (−sin yaw, −cos yaw)
        };
      };
      return {
        station: !!this.station, cruiser: !!this.cruiser,
        stationVisible: this.station?.visible ?? false,
        cruiserVisible: this.cruiser?.visible ?? false,
        stationBody: body(this.station), cruiserBody: body(this.cruiser),
        planets: this.planets.map((p, i) => ({ name: PLANET_DEFS[i].name, visible: p.root.visible, y: Math.round(p.root.position.y - atlas.localY), ...(body(p.root) ?? {}) })),
      };
    };
  }

  /** GLBs load lazily once the connection is live (prefab registry replicated). */
  private ensureModels(): void {
    if (this.tried || !this.assets || !atlas.conn) return;
    const conn = atlas.conn;
    const stId = prefabAssetId(conn, 'prop:space_station');
    const cvId = prefabAssetId(conn, 'prop:ship_sovereign');
    if (stId === null && cvId === null) return; // registry not replicated yet — retry next tick
    this.tried = true;
    const scene = this.app?.world.scene;
    if (!scene) return;
    if (stId !== null) {
      void this.assets.load(stId).then((master) => {
        markShared(master);
        const st = master.clone();
        unlitify(st, new THREE.Color(1.25, 1.2, 1.1)); // warm sunlit metal
        fitLongest(st, deg2size(STATION_DEG));
        st.visible = false;
        scene.add(st);
        this.station = st;
      }).catch(() => { /* model unavailable — the sky just has no station mesh */ });
    }
    if (cvId !== null) {
      void this.assets.load(cvId).then((master) => {
        markShared(master);
        const cv = master.clone();
        unlitify(cv, new THREE.Color(1.1, 1.05, 0.95));
        fitLongest(cv, deg2size(CRUISER_DEG));
        cv.visible = false;
        scene.add(cv);
        this.cruiser = cv;
      }).catch(() => { /* the shader battle layer still shows the engagement */ });
    }
  }

  private buildPlanets(scene: THREE.Scene): void {
    for (const def of PLANET_DEFS) {
      const r = deg2size(def.discDeg) / 2;
      const mat = new THREE.MeshBasicMaterial({ color: new THREE.Color(def.tint.r, def.tint.g, def.tint.b) });
      mat.fog = false;
      const disc = new THREE.Mesh(new THREE.SphereGeometry(r, 24, 16), mat);
      disc.frustumCulled = false;
      // A soft rim so the disc reads as a BODY, not a paint dot (volcanic recipe).
      const rim = new THREE.MeshBasicMaterial({
        color: new THREE.Color(def.tint.r, def.tint.g, def.tint.b),
        transparent: true, opacity: 0.22, side: THREE.BackSide,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });
      rim.fog = false;
      const halo = new THREE.Mesh(new THREE.SphereGeometry(r * 1.25, 20, 12), rim); // (1.5× read as a saucer rim over the rooftops)
      halo.frustumCulled = false;
      const root = new THREE.Group();
      root.add(disc, halo);
      root.visible = false;
      scene.add(root);
      this.planets.push({ root, disc, mat, rim });
      // Stream the real planet face in (the same MinIO textures the space
      // sector renders when you fly there).
      void this.assets?.loadTexture(`textures/planets/${def.tex}.png`).then((t) => {
        mat.map = t; mat.needsUpdate = true;
      }).catch(() => { /* tinted disc is a fine fallback */ });
    }
  }

  private tick(): void {
    if (atlas.spaceFlight || atlas.currentWorldId === null) {
      this.hideAll();
      return;
    }
    this.ensureModels();
    const tod = currentTod();
    const nf = nightFactor(sunDirection(tod).y);
    if (nf < 0.04) { this.hideAll(); return; }
    const nowS = Date.now() / 1000;
    const ax = atlas.localX, ay = atlas.localY, az = atlas.localZ;
    const place = (obj: THREE.Object3D, dir: Vec3): void => {
      obj.position.set(ax + dir.x * R, ay + dir.y * R, az + dir.z * R);
    };
    // ── The station transit ──
    if (this.station) {
      const st = stationAt(nowS);
      const on = st.bright > 0.12 && st.dir.y > 0.02;
      this.station.visible = on;
      if (on) {
        place(this.station, st.dir);
        const t = nowS * 0.13;
        this.station.rotation.set(t * 0.4, t, 0); // slow tumble
      }
    }
    // ── The war cruiser ──
    if (this.cruiser) {
      const bs = battleSkyAt(nowS);
      const on = bs.active > 0.25 && bs.dir.y > 0.05 && nf > 0.5;
      this.cruiser.visible = on;
      if (on) {
        place(this.cruiser, bs.dir);
        // Nose along the heading tangent (hulls nose −Z after lookAt).
        this._v.set(ax + (bs.dir.x + bs.tangent.x * 0.1) * R, ay + (bs.dir.y + bs.tangent.y * 0.1) * R, az + (bs.dir.z + bs.tangent.z * 0.1) * R);
        this.cruiser.lookAt(this._v);
      }
    }
    // ── The planets ──
    const ps = planetsAt(nowS, tod);
    for (let i = 0; i < this.planets.length; i++) {
      const pm = this.planets[i], p = ps[i];
      const on = p.dir.y > -0.02;
      pm.root.visible = on;
      if (!on) continue;
      place(pm.root, p.dir);
      // Disc size tracks proximity; brightness rides night depth (bright
      // planets pierce twilight first, like Venus at dusk).
      const s = Math.max(0.35, Math.min(1.3, p.discDeg / PLANET_DEFS[i].discDeg)); // never past 1.3×: a close pass is a bigger Moon, not a hull
      pm.root.scale.setScalar(s);
      const glow = Math.min(1.6, p.bright) * Math.min(1, nf * 1.6);
      pm.mat.color.setRGB(p.tint.r * glow, p.tint.g * glow, p.tint.b * glow);
      pm.rim.opacity = 0.22 * Math.min(1, nf * 1.4);
    }
  }

  private hideAll(): void {
    if (this.station) this.station.visible = false;
    if (this.cruiser) this.cruiser.visible = false;
    for (const p of this.planets) p.root.visible = false;
  }

  dispose(): void {
    const scene = this.app?.world.scene;
    if (this.station) scene?.remove(this.station);
    if (this.cruiser) scene?.remove(this.cruiser);
    for (const p of this.planets) scene?.remove(p.root);
    this.planets = [];
  }
}
